import { useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { JournalEntryDetail } from "@/components/journal/JournalEntryDetail";
import { DocumentAttachments } from "@/components/documents/DocumentAttachments";
import { useActiveCompany } from "@/lib/company-context";
import { useJournalEntry, useReverseEntry, useVoidEntry } from "@/lib/api/journal.queries";
import { ApiError } from "@/lib/api";

export const Route = createFileRoute(
  "/companies/$companyId/contabilidad/diario/$entryId",
)({
  component: JournalEntryDetailPage,
});

function JournalEntryDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <JournalEntryDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function JournalEntryDetailContent() {
  const { t } = useTranslation();
  const { companyId, entryId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const navigate = useNavigate();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const { data: entry, isLoading, isError } = useJournalEntry(companyId, entryId);
  const { mutateAsync: reverse, isPending: isReversing } = useReverseEntry(companyId);
  const { mutateAsync: voidEntry, isPending: isVoiding } = useVoidEntry(companyId);

  async function handleReverse() {
    if (!entry) return;
    try {
      const reversal = await reverse(entryId);
      toast.success(t("journal.success.reversed"));
      void navigate({
        to: "/companies/$companyId/contabilidad/diario/$entryId",
        params: { companyId, entryId: reversal.id },
      });
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : t("journal.error.reverseFailed");
      toast.error(msg);
    }
  }

  async function handleVoid() {
    try {
      await voidEntry(entryId);
      toast.success(t("journal.success.voided"));
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : t("journal.error.voidFailed");
      toast.error(msg);
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() =>
            void navigate({
              to: "/companies/$companyId/contabilidad/diario",
              params: { companyId },
            })
          }
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-xl font-semibold">{t("journal.entry.detail")}</h1>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : isError ? (
        <ErrorState title={t("journal.error.loadEntry")} />
      ) : entry ? (
        <>
          <JournalEntryDetail
            entry={entry}
            onReverse={handleReverse}
            onVoid={handleVoid}
            isReversingOrVoiding={isReversing || isVoiding}
          />
          {/* Soportes del asiento (el documento físico detrás del registro) — PF-348 */}
          <DocumentAttachments
            companyId={companyId}
            entityType="journal_entry"
            entityId={entryId}
          />
        </>
      ) : null}
    </div>
  );
}
